import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Settings as SettingsIcon,
  Schedule,
  Power,
  PowerOff,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import api from '../services/api';

interface Device {
  id: number;
  device_name: string;
  device_type_id: number;
  power_rating: number;
  status: 'on' | 'off' | 'error' | 'maintenance';
  location?: string;
  farm_id: number;
  created_at: string;
  updated_at?: string;
  device_type?: {
    id: number;
    type_name: string;
    display_name: string;
    power_consumption?: number;
    icon?: string;
  };
}

interface PowerUsageStats {
  total_devices: number;
  active_devices: number;
  total_power_rating: number;
  current_power_usage: number;
  power_efficiency: number;
}

interface MaintenanceModal {
  open: boolean;
  device: Device | null;
}

const DeviceControlPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [powerStats, setPowerStats] = useState<PowerUsageStats>({
    total_devices: 0,
    active_devices: 0,
    total_power_rating: 0,
    current_power_usage: 0,
    power_efficiency: 0
  });
  const [powerData, setPowerData] = useState<Array<{time: string, value: number}>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [maintenanceModal, setMaintenanceModal] = useState<MaintenanceModal>({
    open: false,
    device: null,
  });

  const loadDeviceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [devicesResponse, statsResponse] = await Promise.all([
        api.get('/devices'),
        api.get('/devices/stats/power-usage')
      ]);

      // API 응답이 배열인지 확인하고 방어 코드 추가
      const devicesData = Array.isArray(devicesResponse.data) ? devicesResponse.data : [];
      setDevices(devicesData);
      setPowerStats(statsResponse.data);

      // 실시간 전력 사용량 차트 데이터 생성 (모의 데이터)
      const currentTime = new Date();
      const chartData = Array.from({ length: 15 }, (_, i) => {
        const time = new Date(currentTime.getTime() - (14 - i) * 60000);
        return {
          time: time.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
          value: statsResponse.data.current_power_usage + (Math.random() - 0.5) * 100
        };
      });
      setPowerData(chartData);

    } catch (error: any) {
      console.error('Failed to load device data:', error);
      
      // 인증 에러인 경우 특별 처리
      if (error.response?.status === 403 || error.response?.status === 401) {
        setError('로그인이 필요합니다. 로그인 페이지로 이동해주세요.');
        // 인증 에러 시 로그인 페이지로 리다이렉트할 수 있음
        // window.location.href = '/login';
      } else {
        setError('장치 데이터를 불러오는데 실패했습니다.');
      }
      
      // 에러 발생 시에도 devices를 빈 배열로 유지
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceControl = async (deviceId: number, action: 'turn_on' | 'turn_off') => {
    try {
      await api.post(`/devices/${deviceId}/control`, {
        action: action
      });

      // 데이터 새로고침
      await loadDeviceData();
    } catch (error: any) {
      console.error('Failed to control device:', error);
      setError('장치 제어에 실패했습니다.');
    }
  };

  useEffect(() => {
    loadDeviceData();

    // 30초마다 데이터 새로고침
    const interval = setInterval(loadDeviceData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMaintenanceOpen = (device: Device) => {
    setMaintenanceModal({ open: true, device });
  };

  const handleMaintenanceClose = () => {
    setMaintenanceModal({ open: false, device: null });
  };

  const handleMaintenanceSubmit = () => {
    // 유지보수 설정 저장 로직
    console.log('유지보수 설정 저장:', maintenanceModal.device);
    handleMaintenanceClose();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'on':
        return <Chip label="가동중" color="success" size="small" />;
      case 'off':
        return <Chip label="정지" color="default" size="small" />;
      case 'maintenance':
        return <Chip label="점검" color="warning" size="small" />;
      case 'error':
        return <Chip label="오류" color="error" size="small" />;
      default:
        return <Chip label="알 수 없음" color="default" size="small" />;
    }
  };

  const getMaintenanceProgress = (device: Device) => {
    // 실제로는 별도의 유지보수 기록 테이블에서 계산해야 함
    // 임시로 device ID 기반으로 계산
    return ((device.id * 13) % 100);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ko-KR');
  };

  return (
    <Box>
      {/* Real-time Power Usage Chart */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              실시간 전력 사용량
            </Typography>
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {loading ? '...' : `${Math.round(powerStats.current_power_usage)} W`}
              </Typography>
              <Typography variant="caption">
                {new Date().toLocaleTimeString('ko-KR')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={powerData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#999' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#999' }}
                  domain={[0, Math.max(powerStats.total_power_rating * 1.2, 600)]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#FF6B9D"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Device Control Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              장치별 소비 전력
            </Typography>
            <Button
              variant="outlined"
              onClick={loadDeviceData}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              새로고침
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>장치</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>소비 전력 (W)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>누적 사용시간 (h)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>누적 에너지 (kWh)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>유지 보수</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>제어</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        등록된 장치가 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (Array.isArray(devices) ? devices : []).map((device) => {
                    const maintenanceProgress = getMaintenanceProgress(device);
                    return (
                      <TableRow
                        key={device.id}
                        sx={{
                          '&:hover': { bgcolor: 'grey.50' },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {device.device_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {device.device_type?.display_name || device.device_type?.type_name}
                            </Typography>
                            {device.location && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                위치: {device.location}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {device.power_rating || device.device_type?.power_consumption || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {device.status === 'on' ? '가동중' : '0.0'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {device.status === 'on' ?
                              ((device.power_rating || device.device_type?.power_consumption || 0) * 0.001).toFixed(2) :
                              '0.00'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 60 }}>
                              <LinearProgress
                                variant="determinate"
                                value={maintenanceProgress}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    bgcolor:
                                      maintenanceProgress > 80
                                        ? 'error.main'
                                        : maintenanceProgress > 50
                                        ? 'warning.main'
                                        : 'success.main',
                                  },
                                }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {maintenanceProgress}%
                            </Typography>
                            {getStatusChip(device.status)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              sx={{ color: 'success.main' }}
                              title="전원 켜기"
                              disabled={device.status === 'on' || loading}
                              onClick={() => handleDeviceControl(device.id, 'turn_on')}
                            >
                              <Power />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: 'error.main' }}
                              title="전원 끄기"
                              disabled={device.status === 'off' || loading}
                              onClick={() => handleDeviceControl(device.id, 'turn_off')}
                            >
                              <PowerOff />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: 'primary.main' }}
                              title="유지보수 설정"
                              onClick={() => handleMaintenanceOpen(device)}
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Maintenance Modal */}
      <Dialog
        open={maintenanceModal.open}
        onClose={handleMaintenanceClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            유지 보수
          </Typography>
          <IconButton onClick={handleMaintenanceClose}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {maintenanceModal.device && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                {maintenanceModal.device.device_name} 유지보수 설정
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                장치 ID: {maintenanceModal.device.id}<br/>
                생성일: {formatTimestamp(maintenanceModal.device.created_at)}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>펌프</InputLabel>
                    <Select value="" label="펌프">
                      <MenuItem value="schedule">----년 --월 --일</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>부터</InputLabel>
                    <Select value="" label="부터">
                      <MenuItem value="start">----년 --월 --일</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>까지</InputLabel>
                    <Select value="" label="까지">
                      <MenuItem value="end">----년 --월 --일</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    LED
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl size="small">
                      <InputLabel>부터</InputLabel>
                      <Select value="" label="부터">
                        <MenuItem value="led_start">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>까지</InputLabel>
                      <Select value="" label="까지">
                        <MenuItem value="led_end">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    팬
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl size="small">
                      <InputLabel>부터</InputLabel>
                      <Select value="" label="부터">
                        <MenuItem value="fan_start">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>까지</InputLabel>
                      <Select value="" label="까지">
                        <MenuItem value="fan_end">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    쿨러
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControl size="small">
                      <InputLabel>부터</InputLabel>
                      <Select value="" label="부터">
                        <MenuItem value="cooler_start">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>까지</InputLabel>
                      <Select value="" label="까지">
                        <MenuItem value="cooler_end">----년 --월 --일</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleMaintenanceClose}
            sx={{ borderRadius: 2 }}
          >
            취소
          </Button>
          <Button
            onClick={handleMaintenanceSubmit}
            variant="contained"
            sx={{
              borderRadius: 2,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceControlPage;